// Service for fetching and parsing property data from DOM.com.cy
//
// DOM.com.cy was rebuilt on 1C-Bitrix (server-rendered HTML). The former
// Nuxt SPA and its public GraphQL API (api.staging.app.dom.com.cy) no longer
// exist, so we fetch the listing page through the shared proxy chain (the
// site rejects non-browser User-Agents and sends no CORS headers) and parse
// the markup with DOMParser, the same way BuySellCyprus is handled.

import { createNormalizedPropertyData } from './propertyDataSchema';
import { fetchViaProxies, isNotFoundError } from './proxyFetch';
import { sanitizeText } from './buysellService';

const DOM_BASE_URL = 'https://dom.com.cy';

/**
 * Extract property number from a DOM.com.cy URL.
 * Supported: /en/catalog/sale/199703/, /catalog/rent/199703/, etc.
 */
export const extractDomComId = (url) => {
  if (!url) return null;
  const match = url.match(/\/catalog\/(?:sale|rent)\/(\d+)/i);
  return match ? match[1] : null;
};

/**
 * Canonical English detail-page URL for a listing.
 * @param {string} propertyId
 * @param {'sale'|'rent'} publication
 */
const getCanonicalUrl = (propertyId, publication = 'sale') =>
  `${DOM_BASE_URL}/en/catalog/${publication}/${propertyId}/`;

export const detectDomComPropertyType = (url, normalizedData = null) => {
  if (normalizedData && (normalizedData.forSale || normalizedData.forRent)) {
    return { forSale: normalizedData.forSale, forRent: normalizedData.forRent };
  }

  const urlLower = (url || '').toLowerCase();
  const forSale = urlLower.includes('/sale/');
  const forRent = urlLower.includes('/rent/');

  if (!forSale && !forRent) return { forSale: true, forRent: false };
  return { forSale, forRent };
};

// ---------------------------------------------------------------------------
// HTML helpers
// ---------------------------------------------------------------------------

/**
 * Detect DOM's own "404 Error" page (served with HTTP 404, but some proxies
 * may still hand the body back to us).
 */
const isDomNotFoundPage = (doc) => {
  const title = doc.querySelector('title')?.textContent || '';
  return /^\s*404\b/i.test(title);
};

/**
 * Fetch a DOM.com.cy page as HTML through the proxy chain.
 * Exported so the search scanner can share the same fetch/validation logic.
 * @param {string} url
 * @returns {Promise<string>} - HTML
 */
export const fetchDomComHtml = async (url) => {
  return fetchViaProxies(url, {
    timeout: 20000,
    responseType: 'text',
    label: 'DOM',
    validate: (html) =>
      typeof html === 'string' &&
      html.length > 500 &&
      /<\s*!?\s*doctype|<\s*html/i.test(html),
  });
};

/** Parse "390 000", "1,290,000", "390000" into a number (0 if not parseable). */
export const parseDomPrice = (text) => {
  if (!text) return 0;
  const digits = String(text).replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) : 0;
};

/** Pull the leading number out of "200 m2", "263 m<sup>2</sup>", "1000 m". */
const parseLeadingNumber = (text) => {
  const m = (text || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
  return m ? m[1] : '';
};

/**
 * Read the "Object / Type / City / Bedrooms / ..." table on the detail page.
 * Note: the site's CSS class really is spelled `feautre-item`.
 * @returns {Object<string,string>} - lower-cased label (without colon) -> value text
 */
const parseFeatureTable = (doc) => {
  const features = {};
  // The detail page uses `.feautre-item`; only fall back to the (correctly
  // spelled) listing-card class if that ever gets fixed upstream, so the
  // "similar properties" cards further down the page can't shadow the table.
  let items = doc.querySelectorAll('.feautre-item');
  if (items.length === 0) items = doc.querySelectorAll('.feature-item');
  for (const item of items) {
    const nameEl = item.querySelector('.name') || item.children[0];
    const valueEl = item.querySelector('.value') || item.children[1];
    if (!nameEl || !valueEl) continue;
    const name = (nameEl.textContent || '').replace(/:\s*$/, '').trim().toLowerCase();
    const value = (valueEl.textContent || '').replace(/\s+/g, ' ').trim();
    if (name && value) features[name] = value;
  }
  return features;
};

/** Current price and (optional) crossed-out old price. */
const parsePrices = (doc) => {
  let price = 0;
  const hiddenPrice = doc.querySelector(
    '.element-price-block [itemprop="price"], [itemprop="offers"] [itemprop="price"]'
  );
  if (hiddenPrice) {
    price = parseDomPrice(hiddenPrice.getAttribute('content') || hiddenPrice.textContent);
  }
  if (!price) {
    const mainPrice = doc.querySelector('.element-price-block .price-main .price');
    if (mainPrice) {
      // Strip the "EUR" / "EUR/m2" child spans so we only read the amount.
      const clone = mainPrice.cloneNode(true);
      clone.querySelectorAll('.currency, .vat').forEach((el) => el.remove());
      price = parseDomPrice(clone.textContent);
    }
  }

  let oldPrice = 0;
  const oldPriceEl = doc.querySelector('.element-price-block .price-old .price');
  if (oldPriceEl) {
    const clone = oldPriceEl.cloneNode(true);
    clone.querySelectorAll('.currency, .vat').forEach((el) => el.remove());
    oldPrice = parseDomPrice(clone.textContent);
  }

  return { price, oldPrice };
};

const parseDescription = (doc) => {
  const descEl =
    doc.querySelector('.element-property-wrap.description .description') ||
    doc.querySelector('[itemprop="description"]');
  if (descEl) {
    const text = (descEl.textContent || '').replace(/\s+/g, ' ').trim();
    if (text.length > 10) return text;
  }
  const meta = doc.querySelector('meta[name="description"]');
  return meta?.getAttribute('content') || '';
};

const parseCoordinates = (doc) => {
  const geo =
    doc.querySelector('meta[name="geo.position"]') ||
    doc.querySelector('meta[name="ICBM"]');
  const content = geo?.getAttribute('content') || '';
  const m = content.match(/(-?\d+(?:\.\d+)?)\s*[,;]\s*(-?\d+(?:\.\d+)?)/);
  if (!m) return { latitude: null, longitude: null };
  const lat = parseFloat(m[1]);
  const lng = parseFloat(m[2]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return { latitude: null, longitude: null };
  return { latitude: lat, longitude: lng };
};

/**
 * Convert a parsed DOM.com.cy detail page into normalized app data.
 * @param {Document} doc
 * @param {string} propertyUrl - URL the user supplied (for sale/rent detection)
 */
export function parseDomComPropertyPage(doc, propertyUrl) {
  const features = parseFeatureTable(doc);

  const h1 = doc.querySelector('h1')?.textContent || '';
  const ogTitle = doc.querySelector('meta[property="og:title"]')?.getAttribute('content') || '';
  const title = sanitizeText(h1.trim() || ogTitle, 200);

  const description = sanitizeText(parseDescription(doc), 3000);

  const { price, oldPrice } = parsePrices(doc);

  const area = parseLeadingNumber(features['total area'] || features['indoor area'] || '');
  const bedrooms = parseLeadingNumber(features['bedrooms'] || '');
  const bathrooms = parseLeadingNumber(features['bathrooms'] || '');

  const city = features['city'] || '';
  const district = features['district'] || '';
  const location = city && district ? `${city} / ${district}` : city || district;

  const yearText = parseLeadingNumber(features['year of construction'] || '');
  const constructionYear = yearText ? String(yearText) : 'N/A';

  const ogImage = doc.querySelector('meta[property="og:image"]')?.getAttribute('content') || '';
  const firstSlide = doc.querySelector('.element-photo-slider img, .photo-slide img');
  let imageURL = ogImage || firstSlide?.getAttribute('src') || firstSlide?.getAttribute('data-src') || '';
  if (imageURL && imageURL.startsWith('/')) imageURL = `${DOM_BASE_URL}${imageURL}`;

  const landlordName = sanitizeText(
    doc.querySelector('.element-manager .manager-name')?.textContent || '',
    200
  );

  const { latitude, longitude } = parseCoordinates(doc);

  const { forSale, forRent } = detectDomComPropertyType(propertyUrl);

  const reducedPrice = oldPrice > 0 && price > 0 && oldPrice > price;

  return createNormalizedPropertyData({
    title,
    description,
    price,
    area,
    bedrooms,
    bathrooms,
    imageURL,
    latitude,
    longitude,
    location,
    constructionYear,
    landlordName,
    forSale,
    forRent,
    distress: reducedPrice ? { reducedPrice: true, startPrice: oldPrice } : undefined,
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch a single DOM.com.cy property by scraping its detail page.
 *
 * @param {string} propertyUrl - DOM.com.cy property URL (e.g.
 *   https://dom.com.cy/en/catalog/sale/190736/)
 * @returns {Promise<Object>} - Normalized property data
 */
export const fetchDomComData = async (propertyUrl) => {
  if (!propertyUrl || !propertyUrl.trim()) {
    throw new Error('Property URL is required');
  }

  const propertyId = extractDomComId(propertyUrl);
  if (!propertyId) {
    throw new Error('Invalid DOM.com.cy URL: Cannot extract property ID');
  }

  const { forRent } = detectDomComPropertyType(propertyUrl);
  const fetchUrl = getCanonicalUrl(propertyId, forRent ? 'rent' : 'sale');

  const notFoundMessage =
    `Property #${propertyId} not found on DOM.com.cy. ` +
    'It may have been removed or is no longer active.';

  let html;
  try {
    html = await fetchDomComHtml(fetchUrl);
  } catch (error) {
    // DOM answers a removed listing with HTTP 404; the proxy forwards that.
    if (isNotFoundError(error)) throw new Error(notFoundMessage);
    throw error;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  if (isDomNotFoundPage(doc)) {
    throw new Error(notFoundMessage);
  }

  const data = parseDomComPropertyPage(doc, propertyUrl);

  // A real listing page always carries a price and the feature table.
  if (!data.price && !data.title) {
    throw new Error(notFoundMessage);
  }

  return data;
};
